import { ChangeDetectionStrategy, Component, computed, DestroyRef, OnInit } from '@angular/core';
import { AddYoutubeModalComponent } from './modals/add-youtube-modal/add-youtube-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../store/app.state';
import { YoutubeActions } from '@states/youtube/youtube.action-types';
import { Observable } from 'rxjs';
import { YoutubeSelectors } from '@states/youtube/youtube.selector-types';
import { YoutubeModel } from '@models/youtube.model';
import { Dictionary } from '@ngrx/entity/src/models';
import { smartSearch } from '../../../../helpers/common.helpers';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, UiButtonComponent } from '@ui';
import { SharedEffects } from '@effects/shared.effects';
import { ofType } from '@ngrx/effects';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RouteModalService, UiNoDataComponent } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createYoutubeTableConfig } from './youtube-table.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-youtube',
  templateUrl: './youtube.component.html',
  styleUrl: './youtube.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiKitModule,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
})
export class YoutubeComponent implements OnInit {
  public selectYoutubeList = this.store$.selectSignal(YoutubeSelectors.selectYoutubeList);
  public isInitialLoaded = this.store$.selectSignal(YoutubeSelectors.selectInitialLoaded);
  public selectToRemoveList$: Observable<Dictionary<boolean>> = this.store$.select(
    YoutubeSelectors.selectToRemoveList,
  );
  public selectToRemoveList = this.store$.selectSignal(YoutubeSelectors.selectToRemoveList);

  protected search = syncQueryParam<string>('search');
  protected readonly noResults = features.youtube.noResults;
  protected readonly noData = features.youtube.noData;
  protected readonly loaderColor = PreloaderColor;

  public readonly tableConfig = computed(() => {
    const toRemoveList = this.selectToRemoveList();
    return createYoutubeTableConfig({ toRemoveList: toRemoveList ?? {} });
  });

  public readonly vm$ = computed(() => {
    const isInitialLoaded = this.isInitialLoaded();
    const youtubeList = Object.values(this.selectYoutubeList() ?? {});
    const search = this.search();

    const filteredList = !search
      ? youtubeList
      : smartSearch<YoutubeModel.YoutubeMongoDocument>(youtubeList, search, 'name');

    let viewState: ViewState;
    if (!isInitialLoaded) {
      viewState = 'LOADING';
    } else if (youtubeList.length > 0 && search && !filteredList.length) {
      viewState = 'NO_RESULTS';
    } else if (youtubeList.length > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredList,
      search,
    };
  });

  constructor(
    private dialog: MatDialog,
    private store$: Store<AppState>,
    private sharedEffects$: SharedEffects,
    private translateService: TranslateService,
    private modalService: RouteModalService,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit() {
    this.store$.dispatch(YoutubeActions.resetToInitialState());
    this.store$.dispatch(YoutubeActions.getYoutube());
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.youtube }));

    this.sharedEffects$.confirmation$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(SharedActions.showConfirmModalResultConfirm))
      .subscribe((result) => {
        this.store$.dispatch(YoutubeActions.deleteYoutubeById({ id: result.params['id'] }));
      });

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createYoutube,
          open: () => this.openAddDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public onAdd() {
    this.modalService.openModal(openDialogByParams.createYoutube);
  }

  public delete(youtube: YoutubeModel.YoutubeMongoDocument) {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: this.translateService.instant('CONFIRM_MODALS.COMMON_MSG', { name: youtube.name }),
          title: this.translateService.instant('CONFIRM_MODALS.YOUTUBE_DELETE.TITLE', { name: youtube.name }),
          confirm: 'CONFIRM_MODALS.COMMON_YES',
          disableClose: true,
          params: {
            id: youtube._id,
          },
        },
      }),
    );
  }

  private openAddDialog() {
    return this.dialog.open(AddYoutubeModalComponent, {
      width: '400px',
      height: '336px',
      disableClose: true,
    });
  }

  public onRowAction(event: {
    actionId: string;
    row: YoutubeModel.YoutubeMongoDocument;
  }): void {
    if (event.actionId === 'delete') {
      this.delete(event.row);
    }
  }
}
